import {
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Box,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { AnalyticsService } from '../../../api/services/analyticsService';
import { AnalyticsRow } from '../../../types/api/response/analyticsResponse';
import { CncService } from '../../../api/services/cncService';
import {
  CncCustomerAtBayItem,
  CncAwaitingDispatchItem,
} from '../../../types/api/response/cncresponse';
import { describeAnalyticsError } from '../../../components/Analytics/errors';
import { formatMinutes } from '../../../components/Analytics/format';
import {
  ANALYTICS_DISPATCH_BOARD_ROUTE,
  ANALYTICS_DISPATCH_BOARD_TV_ROUTE,
} from '../../../constants/routes';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  ageColour,
  Empty,
  CncStrip,
  HeroTour,
  MosaicPanel,
  Pill,
  PlainRow,
  Rail,
  StaleCage,
  StopRow,
  useBoardCycle,
  usePagedRows,
  useRiderTurns,
  useRollingRange,
  Scroller,
  StopList,
  PillSep,
} from './MosaicChrome';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ThemeProvider } from '@mui/material/styles';
import { boardTheme } from './boardTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 🚨 SLOT TIMES ARE FIJI CLOCK TIMES. The mart stores UTC instants and the API serves them
 * with a trailing Z, so they must be CONVERTED, never read off the string.
 *
 * Slicing the ISO text — `from.slice(11, 16)` — printed the UTC hour as if it were local and
 * put every slot on this board TWELVE HOURS out: SO-196129's 13:34 window rendered as 01:34,
 * a mid-afternoon delivery shown as the middle of the night. Measured 29 Sep 2026.
 *
 * ⚠️ Pinned to Fiji rather than the viewer's zone. This is a Fiji dispatch board, the platform
 * anchors everything on fiji_date, and the operational day rolls at 06:00 Fiji — a slot read
 * in some other zone is not a slot. It also means the board is correct when tested from
 * outside Fiji, which is how the twelve-hour error survived being looked at.
 */
const FIJI = 'Pacific/Fiji';

/**
 * The live dispatch board (item 1081, wiki: Dispatch Board — Admin Panel UI).
 *
 * ⚠️ FIVE PANELS, NOT SIX. Reassignments arrived with the order-timeline feed. Running late
 * is NOT a panel — it was cut on 14 Sep and is a property on every row instead, shown as a
 * LATE chip wherever a slot appears.
 *
 * ⚠️ The Reassignments panel reads a SECOND view, and its failure is contained: if that call
 * fails the board above still renders. The board is the operational surface; this panel is
 * context, and context must never take the surface down with it.
 *
 * ⚠️ Waiting for a rider is OFTEN EMPTY, and that is not a fault. Locus has no packed signal
 * at all — openedAt and planningAt both fire ~2 seconds after an order is received — so
 * packed_at comes from SuperStore's own packing records, through the analytics packing
 * connector (DA §4.7, W-13/W-14). That path IS wired up and verified.
 * 🚨 The panel still reads empty wherever the two windows do not overlap. On DEV, measured 29
 * Sep 2026: fact_order_packing holds 14 rows ending 7 Sep while the board covers 14–29 Sep, so
 * packed_age_min is null on all 214 rows. An empty panel here is a DATA range question, never
 * evidence that the connector is missing — the panel says so rather than looking broken.
 */

/** Matches ingestion (Dispatch Analytics §4.5). Slower and the board is quietly wrong. */
const REFRESH_MS = 2 * 60 * 1000;

const STALE_AFTER_MINUTES = 10;

/**
 * The MINIMUM seconds a rider holds the card. Override with ?secs=8.
 *
 * 🚨 30, NOT 5 (§1.1): "nothing turns faster than 30 seconds... the first cut turned every
 * 5–10s and was unreadable across a room." That lesson was already paid for once.
 *
 * ⚠️ A floor, not the dwell. A rider stays until their whole list has scrolled past, so one
 * with nine orders stays longer than one with three — which is the point. This number only
 * governs the riders whose orders fit the card and so never scroll at all.
 *
 * In the URL rather than a constant because the television is tuned by whoever is standing in
 * front of it — the right dwell depends on the screen, the viewing distance and how busy the
 * day is, and none of those are worth a deploy. Clamped, so a typo can neither freeze the
 * board on one rider nor flicker through six a second.
 */
const DEFAULT_ROTATE_SECONDS = 30;
const MIN_ROTATE_SECONDS = 2;
const MAX_ROTATE_SECONDS = 60;

/**
 * ⚠️ `?scroll=` IS GONE, and with it the pixels-per-second knob. Nothing on this board scrolls
 * any more: every panel measures how many whole rows fit and turns pages, because a row sliced
 * across the bottom edge of a television is unreadable and a creeping list is worse. Pace is
 * now one setting, ?secs, for the whole board.
 */

/** How long the wall display waits, with nobody touching it, before hiding its controls. */
const CONTROLS_IDLE_MS = 10 * 1000;

/**
 * The longest any one rider may hold the screen, whatever their list. Override with ?maxsecs=.
 *
 * ⚠️ Purely a backstop. Reaching it means a rider's last orders WERE cut off — the thing the
 * pass-complete signal exists to prevent — so it is set well clear of anything real. The
 * busiest rider measured, Brian on 30 Sep, carried 13 stops.
 */
const DEFAULT_MAX_RIDER_SECONDS = 150;

/**
 * Nikhil's limits, 30 Oct (§38). Each is a number rather than a rule, so changing one is a
 * number and not a rewrite.
 */
const CNC_STRIP_MAX = 3;
const CNC_PIN_MAX = 3;
/** An unpacked C&C order flashes once it has gone unpacked this long. */
const CNC_UNPACKED_FLASH_MIN = 10;

/** How many days the picker opens on. Two = yesterday and today. */
const DEFAULT_WINDOW_DAYS = 2;

/**
 * How far back the picker may reach. Mirrors LOCUS_LIVE_LOOKBACK_DAYS.
 *
 * 🚨 NOT a presentation choice. Beyond this the connector stops re-reading orders, so one
 * cancelled nine days ago would still read PARKED — the precise failure that had riders shown
 * delivering orders they had finished two days earlier (2 Oct 2026). Greying those dates out
 * is how the picker stays unable to ask a question we cannot answer honestly. Raise this and
 * the server setting together or not at all.
 */
const MAX_LOOKBACK_DAYS = 14;

/** Remembered per browser, so a television comes back the way the room left it. */
const THEME_KEY = 'ss_dispatch_daylight';

type Panel = 'ASSIGNED' | 'ACTIVE_TOUR' | 'WAITING_FOR_RIDER' | 'HELD_BACK';

const str = (row: AnalyticsRow, key: string): string =>
  row[key] === null || row[key] === undefined ? '' : String(row[key]);

const num = (row: AnalyticsRow, key: string): number | null =>
  row[key] === null || row[key] === undefined ? null : Number(row[key]);

/** "1 rider", "2 riders". A wall screen reading "1 riders" looks unfinished from the floor. */
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Groups rows by rider so a dispatcher reads "who has what", not a flat list. */
const byRider = (rows: AnalyticsRow[]): [string, AnalyticsRow[]][] => {
  const groups = new Map<string, AnalyticsRow[]>();
  rows.forEach((row) => {
    // ⚠️ Group by the rider NAME the mart resolved, never by parsing the tour id: a tour id
    // looks like <name>-<date>-<n> and that name is wrong often enough to mislead. Locus names
    // a tour when it is CREATED and then freezes that name while dispatch moves the tour to
    // someone else — 4 of 29 assigned tours carried another rider's name on 30 Sep 2026.
    const rider = str(row, 'rider_name') || 'Unassigned';
    groups.set(rider, [...(groups.get(rider) ?? []), row]);
  });
  return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
};

export const DispatchBoardPage = ({ tv = false }: { tv?: boolean }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hops, setHops] = useState<AnalyticsRow[]>([]);
  const [hopsFailed, setHopsFailed] = useState(false);
  const [truncated, setTruncated] = useState(false);

  /**
   * 🚨 A SECOND SOURCE, AND ITS FAILURE IS CONTAINED. The bay and the arrival time live in
   * SuperStore's order database, not Locus. If this call fails the list stays empty, the
   * escalation simply does not fire, and the five Locus-fed panels are untouched — a
   * SuperStore dependency may never take down a Locus-sourced screen (§4.7.3).
   */
  const [cncQueue, setCncQueue] = useState<CncCustomerAtBayItem[]>([]);
  const [cncAwaiting, setCncAwaiting] = useState<CncAwaitingDispatchItem[]>([]);
  /** One row per rider per day. The rail's day totals, which the board mart cannot answer. */
  const [totals, setTotals] = useState<AnalyticsRow[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Dark by default, remembered in the browser (Nikhil, 23 Oct). This supersedes the 06:00/18:00
   * auto-switch: a television restarts and must come back the way the room left it, and the
   * clock cannot know whether the roller door is open.
   *
   * ⚠️ Reads can throw — a private window, blocked site data — and a board that failed to open
   * because it could not remember a colour would be a poor trade.
   */
  const [daylight, setDaylight] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(THEME_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleTheme = () => {
    setDaylight((on) => {
      const next = !on;
      try {
        window.localStorage.setItem(THEME_KEY, next ? '1' : '0');
      } catch {
        // Not remembering is survivable; refusing to switch is not.
      }
      return next;
    });
  };

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const BOARD_LIMIT = 2000;
        const response = await AnalyticsService.queryView('dispatch-board', { limit: BOARD_LIMIT });
        // 🚨 A CAP THAT CUTS SILENTLY IS WORSE THAN A LOW ONE. Coming back with exactly as many
        // rows as we asked for means there were probably more, and nothing else on the board
        // would say so (Nikhil, 23 Oct).
        setTruncated(response.rows.length >= BOARD_LIMIT);
        setRows(response.rows);
        setAsOf(response.as_of);

        // Contained failure: a problem here leaves the board above intact. Newest first and
        // capped at 50 — a hop from yesterday is still worth seeing on a quiet morning, and
        // every row states its own age, so an old one cannot pass for new.
        try {
          const moves = await AnalyticsService.queryView('dispatch-reassignments', {
            sort: '-event_at',
            limit: 50,
          });
          setHops(moves.rows);
          setHopsFailed(false);
        } catch {
          setHopsFailed(true);
        }

        // 🚨 BOTH QUEUES, because the stage comes from SuperStore and not from Locus (§38,
        // Nikhil 30 Oct). A C&C order stays RECEIVED in Locus after it is packed and only moves
        // when the customer checks in — so Locus cannot tell packed from unpacked, and an order
        // absent from BOTH queues is one nobody has packed yet.
        try {
          const from = dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss');
          const to = dayjs().add(1, 'day').endOf('day').format('YYYY-MM-DDTHH:mm:ss');
          // ⚠️ SETTLED, NOT ALL. One queue failing must not discard the other: losing the bay
          // queue costs a customer who IS at a bay their bay, their arrival time and their
          // stage, and the strip then calls them unpacked — the opposite of escalating them.
          const [atBay, awaiting] = await Promise.allSettled([
            CncService.getCustomerAtBay(undefined, from, to),
            CncService.getAwaitingDispatch(undefined, from, to),
          ]);
          setCncQueue(atBay.status === 'fulfilled' ? (atBay.value ?? []) : []);
          setCncAwaiting(awaiting.status === 'fulfilled' ? (awaiting.value ?? []) : []);
        } catch {
          setCncQueue([]);
          setCncAwaiting([]);
        }

        // Contained in its own right, for the same reason: the rail's day totals are context.
        // A failure here must leave the five panels standing, so the pills simply go absent
        // rather than the board going down for them.
        try {
          const perf = await AnalyticsService.queryView('dispatch-performance', { limit: 2000 });
          setTotals(perf.rows);
        } catch {
          setTotals([]);
        }
      } catch (e: any) {
        // 🚨 SILENT ON THE WALL DISPLAY (Nikhil, 23 Oct). Nobody stands in front of a television
        // to read a toast, and a dropped poll does not mean the board is wrong — it still holds
        // the last good read, and the rail ages and then takes the screen over. The page keeps
        // the message, because someone is sitting there and can act on it.
        if (!tv) {
          dispatch(
            showNotification({
              message: describeAnalyticsError(e, { fallback: 'Failed to load the dispatch board' }),
              severity: 'error',
            }),
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch, tv],
  );

  useEffect(() => {
    load();
    // Nobody clicks a dispatch board — it has to refresh itself, on ingestion's cadence.
    timer.current = setInterval(() => load(true), REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  /**
   * EVERYTHING is shown, internal orders included (Justin, 1 Oct).
   *
   * The fulfilment manager wants the whole picture on the wall, and inside a two-day window
   * internal orders are a small, mostly parked minority — 12 of 67 rows measured on 1 Oct,
   * every one of them in Held back — rather than the quarter of live orders they are across a
   * fortnight.
   *
   * ⚠️ dq_test_order still rides on every row and nothing downstream changed to hide it, so
   * restoring the operational-only view costs a filter and nothing else.
   */
  /**
   * The days on show. Yesterday and today by default (Justin, 2 Oct), changed with the picker.
   *
   * ⚠️ Filtered HERE, not in the query. The mart holds a fortnight of unfinished work, a few
   * hundred rows, so the whole set is already in the browser — re-querying on every change
   * would put a spinner on a wall screen to filter rows it was holding all along.
   *
   * ⚠️ Fiji, not the viewer's zone. fiji_date is Locus's own dispatch date, so "today" has to
   * mean today in Suva, or a laptop in Manila quietly picks the wrong two days.
   */
  const [range, setRange] = useRollingRange(
    () => {
      const today = dayjs().tz(FIJI).startOf('day');
      return { from: today.subtract(DEFAULT_WINDOW_DAYS - 1, 'day'), to: today };
    },
    // Fiji, not the viewer's zone — the board's day is the warehouse's day, and a browser in
    // Manila rolls over four hours early.
    () => dayjs().tz(FIJI).format('YYYY-MM-DD'),
  );

  // Plain YYYY-MM-DD comparison: fiji_date is a date, never an instant, so there is no
  // conversion here to get wrong.
  const fromKey = range.from.format('YYYY-MM-DD');
  const toKey = range.to.format('YYYY-MM-DD');
  /**
   * fiji_date as a plain YYYY-MM-DD, whatever shape it arrives in.
   *
   * 🚨 IT IS NOT ALWAYS A PLAIN DATE ON THE WIRE. fiji_date is a SQL DATE and JsonTimeConfig
   * only covers LocalDateTime, so it serialises as an instant at midnight in the SERVICE'S own
   * zone. On a host running Manila time, 1 Oct arrived as "2026-09-30T16:00:00Z" — which sorts
   * BELOW the string "2026-10-01" and silently dropped every row for that day. The board read
   * 4 held-back orders where the mart held 13, while 2 Oct came through untouched, so the
   * board looked merely quiet rather than broken (measured 2 Oct 2026).
   *
   * Reading it as an instant and converting to Fiji lands on the right day either way, so this
   * holds whether the service sends a date or a timestamp, and on any host zone.
   */
  const dayKey = (r: AnalyticsRow): string => {
    const raw = str(r, 'fiji_date');
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : dayjs(raw).tz(FIJI).format('YYYY-MM-DD');
  };

  const inRange = (r: AnalyticsRow) => {
    const d = dayKey(r);
    return d >= fromKey && d <= toKey;
  };

  const visible = rows.filter(inRange);

  const panel = (name: Panel) => visible.filter((r) => str(r, 'panel') === name);
  const assigned = panel('ASSIGNED');
  const active = panel('ACTIVE_TOUR');
  const waiting = panel('WAITING_FOR_RIDER');

  /**
   * ⚠️ STALEST FIRST, NOT SOONEST SLOT (§5.9). A slot sort surfaces the tightest window, which
   * dispatch usually has in hand already. The order sitting packed with nobody on it is the one
   * nobody is looking at, and that delay appears nowhere else on the screen.
   */
  // ⚠️ DECLARED HERE, ABOVE THE FIRST SORT THAT USES IT. A comparator runs during render, and a
  // const referenced above its own declaration throws — silently, because sort skips the
  // comparator entirely on a one-row array, so it only appears once the panel has two.
  const isCnc = (r: AnalyticsRow) => str(r, 'sla_tier') === 'cnc';

  // ⚠️ C&C FIRST, THEN STALEST (§38). A Click & Collect order is somebody standing at the
  // collection point; a packed order waiting for a rider is stock on a bench. Age decides the
  // rest, as the panel's caption says.
  const waitingRows = [...waiting].sort(
    (a, b) =>
      Number(isCnc(b)) - Number(isCnc(a)) ||
      (num(b, 'packed_age_min') ?? 0) - (num(a, 'packed_age_min') ?? 0),
  );
  /**
   * 🚨 INTERNAL ORDERS ARE HIDDEN HERE AND COUNTED IN THE HEADER (Nikhil, 23 Oct). They were
   * 31 of 36 parked on 22 Oct, and the day totals beside them exclude internal by design, so
   * the panel and the strip were counting different populations with nothing saying so (DB-17).
   *
   * ⚠️ The count is kept rather than dropped. A panel that silently loses five sixths of its
   * rows is the defect this board exists to prevent; "31 internal, hidden" is the difference,
   * stated.
   */
  const heldAll = panel('HELD_BACK');
  const held = heldAll.filter((r) => num(r, 'dq_test_order') !== 1);
  const heldInternal = heldAll.length - held.length;
  const restockCount = held.filter((r) => str(r, 'hold_kind') === 'RESTOCK').length;
  const overdue = visible.filter((r) => num(r, 'is_overdue') === 1).length;

  /**
   * 🚨 "HAS ANYTHING EVER BEEN PACKED" IS THE WRONG QUESTION. The old test asked only whether a
   * packing age existed anywhere, and parked orders keep theirs forever: on 26 Oct DEV held 48
   * rows packed between 12 and 16 October — fossils from before the feed stopped on the 15th —
   * so the rail read "0 waiting" beside a panel saying the feed was not connected. Zero is a
   * measurement, and on a wall it reads as all-clear.
   *
   * ⚠️ 24 hours, against a packing pull that runs on a rolling 12-hour window: generous enough
   * that one slow night does not blank the pill, short enough that a ten-day-old pack cannot
   * pass for a live feed.
   */
  const packingFeedLive = rows.some((r) => {
    const packedAge = num(r, 'packed_age_min');
    return packedAge !== null && packedAge < 24 * 60;
  });

  /**
   * 🚨 DB-17: the two halves of the rail count different orders. The live pills come from the
   * board mart, which INCLUDES internal and staff orders; the day totals come from
   * dispatch-performance, which excludes them. Measured 21 Oct 2026: 31 of 43 rows in range
   * were internal, all parked.
   *
   * ⚠️ Switched off 21 Oct (Justin), back on 23 Oct (Nikhil, who is taking the reversal to him).
   */
  const internal = visible.filter((r) => num(r, 'dq_test_order') === 1).length;

  // Parked work the chosen range leaves out — stated on the panel rather than silently
  // dropped, because a board that hides 150 orders without saying so is the kind of thing a
  // wall screen exists to prevent.
  const olderHeld = rows.filter(
    (r) => str(r, 'panel') === 'HELD_BACK' && dayKey(r) < fromKey,
  ).length;

  // Trimmed here rather than in the mart: this view is shared with the standalone
  // reassignments report, which keeps its full history. Two days on the wall, everything
  // on the report page. Same range as the board above, so the strip never disagrees with the
  // panels beside it.
  /**
   * 🚨 THE OPERATIONAL DAY, NOT THE PICKER (§5.1, Nikhil 23 Oct). Before 06:00 the team is still
   * working the previous delivery day while the new day's orders arrive, so "today" is BOTH
   * calendar days until the roll and today alone after it.
   *
   * ⚠️ Applies to the day totals and Reassignments only. The live panels and parked work stay on
   * the picker's range — somebody widening it to look something up must still see the rows.
   */
  const opDay = (() => {
    const now = dayjs().tz(FIJI);
    const today = now.startOf('day');
    const from = now.hour() >= 6 ? today : today.subtract(1, 'day');
    return { from: from.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
  })();
  const inOpDay = (r: AnalyticsRow) => {
    const d = dayKey(r);
    return d >= opDay.from && d <= opDay.to;
  };

  const visibleHops = hops.filter(inOpDay);
  const humanHops = visibleHops.filter((r) => str(r, 'actor_kind') === 'HUMAN').length;

  const rotateSeconds = Math.min(
    MAX_ROTATE_SECONDS,
    Math.max(MIN_ROTATE_SECONDS, Number(params.get('secs')) || DEFAULT_ROTATE_SECONDS),
  );

  const assignedRiders = byRider(assigned);
  const activeRiders = byRider(active);

  const maxRiderSeconds = Math.min(
    300,
    Math.max(rotateSeconds, Number(params.get('maxsecs')) || DEFAULT_MAX_RIDER_SECONDS),
  );

  /**
   * 🚨 ONE CLOCK, AND THE RIDER CARDS ARE ON IT TOO. Separate timers meant separate start
   * times: the rotations and the pages drifted apart, and a rider card could hold the screen
   * for forty-five seconds while the four beside it turned every five. The backstop is in
   * BEATS now, because beats are the only unit this board has left.
   */
  const pageSeconds = rotateSeconds;
  // Staggered across the beat so the five panels do not all flip on the same frame.
  const idleBeat = useBoardCycle(pageSeconds, tv, 0);
  const heroBeat = useBoardCycle(pageSeconds, tv, pageSeconds * 0.2);
  const waitBeat = useBoardCycle(pageSeconds, tv, pageSeconds * 0.4);
  const movesBeat = useBoardCycle(pageSeconds, tv, pageSeconds * 0.6);
  const holdBeat = useBoardCycle(pageSeconds, tv, pageSeconds * 0.8);
  const maxRiderBeats = Math.max(1, Math.round(maxRiderSeconds / pageSeconds));

  /**
   * 🚨 BOTH THE RIDER AND THE PAGE ARE ARITHMETIC ON THE BEAT, the way Reassignments has always
   * worked. The previous version asked the card whether everything in it had been seen, through
   * a callback and a flag, and that answer could arrive early — a page count that dipped to 1
   * for a single frame released the turn, and the rider changed before page two was drawn.
   * Nothing is asked now, so nothing can answer wrongly.
   *
   * ⚠️ Each rider gets maxRiderBeats, fixed. A rider needing more pages than that has its tail
   * cut — the documented backstop, and the reason maxRiderBeats is worth keeping generous.
   */
  /**
   * ⚠️ THE PAGE COUNT COMES BACK FROM THE PAGER, one render behind, and that is fine: the turn
   * is only ever ended by a count the fitter has vouched for, so a stale one holds the rider
   * rather than cutting them short.
   */
  const [assignedFit, setAssignedFit] = useState({ pages: 1, measured: false });
  const [activeFit, setActiveFit] = useState({ pages: 1, measured: false });

  const assignedTurn = useRiderTurns(
    assignedRiders.length,
    assignedFit.pages,
    assignedFit.measured,
    idleBeat.cycle,
    tv,
    maxRiderBeats,
  );
  const activeTurn = useRiderTurns(
    activeRiders.length,
    activeFit.pages,
    activeFit.measured,
    heroBeat.cycle,
    tv,
    maxRiderBeats,
  );

  const assignedIndex = assignedTurn.index;
  const activeIndex = activeTurn.index;
  const assignedTurnBeat = assignedTurn.turnBeat;
  const activeTurnBeat = activeTurn.turnBeat;

  /**
   * Stops by slot end, soonest first.
   *
   * ⚠️ ONLY THE SLOT ORDER IS CLAIMED. Locus exposes no route position on a task, so the board
   * cannot say what order a rider will actually drop in — the prototype inferred it from slot
   * time and was wrong (DB-15). Sorting by slot is true and useful; captioning it as the drop
   * order would be the same mistake in words. Orders with no slot sort last.
   */
  /** A return order — Locus `skills = rtn`, order number ending in R (Nikhil, 23 Oct). */
  const isReturn = (r: AnalyticsRow) => str(r, 'sla_tier') === 'rtn';

  /**
   * 🚨 `skills = cnc` FROM LOCUS IS ENOUGH (§38, 30 Oct). This REVERSES §33's "both sides must
   * agree": a live C&C order was invisible until a person assigned it, and a customer standing
   * at a collection point is the one thing on this board that must never be missed. The queues
   * still supply bay, arrival and stage — their absence degrades the detail, never the alarm.
   */

  const atBayByOrder = new Map(cncQueue.map((item) => [item.order_no, item]));
  const awaitingByOrder = new Map(cncAwaiting.map((item) => [item.order_no, item]));

  /**
   * ⚠️ THE BAY IS NEVER BLANK. The queue knows it when the customer has checked in; before that
   * the Locus address carries it ("Bay 1 - Click & Collect, Kings Road"); and some collection
   * points have no bay at all (SO-204895, "Superstore Collection Point").
   */
  const bayLabel = (r: AnalyticsRow): string => {
    const queued = atBayByOrder.get(str(r, 'order_id'));
    if (queued?.bay?.label) return queued.bay.label.toUpperCase();
    // ⚠️ A SPACE AND A NUMBER, both required. `\s*` matches zero spaces, so "Bayview Road"
    // parsed as bay "view" and a street name rendered as a bay badge.
    const fromAddress = str(r, 'address_text').match(/\bbay\s+(\d+)\b/i);
    return fromAddress ? `BAY ${fromAddress[1]}` : 'C&C POINT';
  };

  /**
   * 🚨 THE STAGE COMES FROM SUPERSTORE, NOT LOCUS (§38, corrected 30 Oct). Locus keeps a C&C
   * order at RECEIVED through packing and only moves it when the customer checks in, so Locus
   * cannot distinguish "nobody has packed this" from "packed and waiting for its customer".
   * Absent from both queues IS the unpacked state.
   */
  const cncState = (r: AnalyticsRow) => {
    const id = str(r, 'order_id');
    const atBay = atBayByOrder.get(id);
    const panelName = str(r, 'panel');
    const rider = str(r, 'rider_name');
    const arrived = atBay?.arrived_at ? dayjs(atBay.arrived_at) : null;
    const sinceArrival = arrived?.isValid() ? dayjs().diff(arrived, 'minute') : null;
    const age = num(r, 'received_age_min');

    const here = atBay?.order_status === 'CUSTOMER_AT_BAY';
    const atGate = atBay?.order_status === 'CUSTOMER_AT_GATE';
    const packed = awaitingByOrder.has(id);
    const unpacked = !atBay && !packed;

    const riderStage = !rider
      ? 'NO RIDER YET'
      : panelName === 'ACTIVE_TOUR'
        ? `${rider} · ON THE WAY TO BAY`
        : `${rider} · TO LOAD`;

    /**
     * 🚨 LOCUS CARRIES THE STAGE WHEN SUPERSTORE CANNOT (§39, Nikhil 30 Oct). DEV's C&C queue
     * never holds production orders, so §38's rule left every order reading NOT PACKED YET for
     * its whole life — and C&C goes live on the DEV board tonight. Locus only advances a C&C
     * order at bay check-in (§38 correction), so its status is a true stage:
     *
     *     RECEIVED           → waiting for its customer, nobody there yet
     *     OPEN / PLANNING    → customer has scanned the bay QR
     *     ASSIGNED           → a CSR has given it to a rider
     *     EXECUTING          → the rider is walking it out
     *
     * ⚠️ This is also the production fallback if the CNC queue call fails, which is why it is
     * not gated on the environment.
     *
     * ⚠️ LOCUS CANNOT SEE "packed" OR "at gate". Those two readings are lost whenever this
     * branch is taken — ops watch the admin CNC Dashboard for them during go-live.
     */
    const locusStage =
      str(r, 'status') === 'ASSIGNED' && rider
        ? `${rider} · TO LOAD`
        : str(r, 'status') === 'EXECUTING' && rider
          ? `${rider} · ON THE WAY TO BAY`
          : 'AT BAY · NO RIDER YET';

    /** True only while SuperStore can speak for this order. */
    const ourStatus = Boolean(atBay) || packed;

    /**
     * ⚠️ NO STAGE IN HELD BACK. The panel already says parked, and the queues cannot describe
     * an order that has left the C&C flow — SO-204889 was assigned and then parked, yet reads
     * as "not packed yet" because DEV's queue never held it. The parking reason is what that
     * panel exists to show, so the row keeps it and the chip alone marks it as C&C.
     */
    const stage =
      panelName === 'HELD_BACK'
        ? ''
        : here
          ? riderStage
          : atGate
            ? 'AT GATE'
            : packed
              ? 'PACKED'
              : locusStage;

    return {
      order: id,
      bay: bayLabel(r),
      stage,
      unpacked,
      /**
       * Tied to the stage rather than to `unpacked`: "not packed" is a reading only SuperStore
       * can give, and it is unreachable while Locus is standing in.
       */
      flashing: stage === 'NOT PACKED YET' && age !== null && age >= CNC_UNPACKED_FLASH_MIN,
      waitingMin: sinceArrival ?? age,
      /**
       * 🚨 WHOSE CLOCK THIS IS. §38 counts from planning when there is no arrival, and that
       * stays — but the words do not. "customer waiting 3h 36m" on an order nobody has packed
       * says a person is standing at the collection point when none is, and it is the one
       * number on this strip somebody will act on.
       */
      // Every C&C order that reaches the board has checked in — RECEIVED never gets this far.
      customerHere: here || atGate || !ourStatus,
      /**
       * ⚠️ OFF THE STRIP IN TWO CASES, both Nikhil's call on 30 Oct. A packed order whose
       * customer has not arrived has nothing anyone can act on; a parked one is held for a
       * reason and stays red in Held back instead. Everything else is somebody waiting.
       */
      onStrip: !packed && panelName !== 'HELD_BACK',
    };
  };

  const cncRows = visible.filter(isCnc);
  const cncStateByOrder = new Map(cncRows.map((r) => [str(r, 'order_id'), cncState(r)]));
  const cncOnStrip = cncRows
    .map((r) => cncStateByOrder.get(str(r, 'order_id'))!)
    .filter((s) => s.onStrip)
    // ⚠️ PEOPLE BEFORE ORDERS. The strip caps at three lines, and a customer who is physically
    // at the collection point must never lose one to an order that has gone unpacked longer.
    .sort(
      (a, b) =>
        Number(b.customerHere) - Number(a.customerHere) ||
        (b.waitingMin ?? 0) - (a.waitingMin ?? 0),
    );

  /** Riders whose card holds something that must be noticed from another tab. */
  /** The dot: anything that must be noticed from another tab (Nikhil, 23 Oct). */
  const alertRiders = Array.from(
    new Set(
      visible
        .filter((r) => num(r, 'is_overdue') === 1 || isCnc(r))
        .map((r) => str(r, 'rider_name') || 'Unassigned'),
    ),
  );

  /**
   * ⚠️ THE RED NAME IS C&C ONLY (§38). Colouring it for overdue as well turned most tabs red on
   * a busy evening — 7 overdue across 4 riders on 30 Oct — and a signal every tab carries is
   * not a signal. Overdue keeps the dot it already had.
   */
  const cncRiders = Array.from(
    new Set(visible.filter(isCnc).map((r) => str(r, 'rider_name') || 'Unassigned')),
  );

  const bySlot = (a: AnalyticsRow, b: AnalyticsRow) => {
    const x = str(a, 'slot_end');
    const y = str(b, 'slot_end');
    if (!x && !y) return 0;
    if (!x) return 1;
    if (!y) return -1;
    return x < y ? -1 : x > y ? 1 : 0;
  };

  /**
   * 🚨 RETURNS FIRST, THEN SLOT ORDER. A return means the first delivery was wrong, so it
   * outranks a normal drop (Nikhil, 23 Oct). The caption changes with it, because a list whose
   * order has changed and does not say so is the DB-15 mistake in a different costume.
   */
  const byPriority = (a: AnalyticsRow, b: AnalyticsRow) => {
    // A customer standing in a bay outranks a return, which outranks a normal drop.
    const ca = isCnc(a);
    const cb = isCnc(b);
    if (ca !== cb) return ca ? -1 : 1;
    const ra = isReturn(a);
    const rb = isReturn(b);
    if (ra !== rb) return ra ? -1 : 1;
    return bySlot(a, b);
  };

  // Oldest first — the panel's caption says so, and the longest wait is the one being chased.
  //
  // ⚠️ EXCEPT C&C (§38), which pins ahead of age. A parked Click & Collect order drops off the
  // red strip on Nikhil's call but stays red here, and sorting it by age buried it on page 3 of
  // 3 — visible for ten seconds in every ninety, which is the same as hidden.
  const heldSorted = [...held].sort(
    (a, b) =>
      Number(isCnc(b)) - Number(isCnc(a)) ||
      (num(b, 'hold_age_min') ?? 0) - (num(a, 'hold_age_min') ?? 0),
  );

  const assignedRider = assignedRiders[assignedIndex];
  const activeRider = activeRiders[activeIndex];
  const assignedStops = tv && assignedRider ? [...assignedRider[1]].sort(byPriority) : [];
  const activeStops = tv && activeRider ? [...activeRider[1]].sort(byPriority) : [];

  // byPriority sorts C&C and returns to the front, so the leading rows ARE the pinned ones.
  const pinnedCount = (stops: AnalyticsRow[]) =>
    // ⚠️ CAPPED (Nikhil, 30 Oct). Four C&C orders on one rider would pin the whole card and
    // leave nothing rotating; the rest stay red, they just page like any other stop.
    Math.min(CNC_PIN_MAX, stops.filter((r) => isCnc(r) || isReturn(r)).length);
  // ⚠️ THE TURN'S OWN BEAT, not the board's, so the page count restarts with each rider. The
  // rider's name keys the measurement, so two riders with the same number of stops still count
  // as different cards.
  const assignedPaged = usePagedRows(
    assignedStops,
    assignedTurnBeat,
    tv,
    pinnedCount(assignedStops),
    assignedRider?.[0] ?? '',
  );
  const activePaged = usePagedRows(
    activeStops,
    activeTurnBeat,
    tv,
    pinnedCount(activeStops),
    activeRider?.[0] ?? '',
  );

  useEffect(() => {
    setAssignedFit((was) =>
      was.pages === assignedPaged.pages && was.measured === assignedPaged.measured
        ? was
        : { pages: assignedPaged.pages, measured: assignedPaged.measured },
    );
  }, [assignedPaged.pages, assignedPaged.measured]);
  useEffect(() => {
    setActiveFit((was) =>
      was.pages === activePaged.pages && was.measured === activePaged.measured
        ? was
        : { pages: activePaged.pages, measured: activePaged.measured },
    );
  }, [activePaged.pages, activePaged.measured]);
  const waitingPaged = usePagedRows(
    waitingRows,
    waitBeat.cycle,
    tv,
    Math.min(CNC_PIN_MAX, waitingRows.filter(isCnc).length),
  );
  const hopsPaged = usePagedRows(visibleHops, movesBeat.cycle, tv);
  const heldPaged = usePagedRows(
    heldSorted,
    holdBeat.cycle,
    tv,
    Math.min(CNC_PIN_MAX, heldSorted.filter(isCnc).length),
  );

  /**
   * The rail's day totals, summed over the SAME range the panels show.
   *
   * 🚨 DB-17 IS WHY THEY ARE SUMMED OVER THE RANGE RATHER THAN "TODAY". Two sets of figures on
   * one strip, computed over different populations with nothing saying so, is the exact defect
   * already found on this rail once.
   *
   * ⚠️ THEY STILL COUNT A DIFFERENT POPULATION, and it cannot be helped: dispatch-performance
   * excludes internal and test orders, while the live pills beside them include everything
   * (Justin, 1 Oct). The dim `internal` pill is what reconciles the two, which is the whole
   * reason it is on the rail. Each pill carries a title saying which set it counts.
   */
  const totalsInRange = totals.filter(inOpDay);
  const sumOf = (key: string) => totalsInRange.reduce((n, r) => n + (num(r, key) ?? 0), 0);
  const ordersInRange = sumOf('orders_total');
  const deliveredInRange = sumOf('orders_completed');
  const cancelledInRange = sumOf('orders_cancelled');

  /**
   * 🚨 FULLSCREEN ONLY ANSWERS INSIDE A CLICK. The browser refuses the request from anywhere
   * else, so arriving at /tv any other way — typing the address, a reload, a television
   * restoring its last tab after a power cut — lands on the board with the browser's own
   * chrome still showing. No script can cover that case, which is why the button lives on
   * BOTH surfaces rather than only on the one that navigates here.
   */
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));

  useEffect(() => {
    // Tracked rather than assumed: Esc and F11 leave fullscreen without touching our buttons,
    // and a board still offering "Fullscreen" while already fullscreen reads as broken.
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const goFullscreen = () => document.documentElement.requestFullscreen?.().catch(() => undefined);

  const enterTv = () => {
    goFullscreen();
    navigate(`${ANALYTICS_DISPATCH_BOARD_TV_ROUTE}?secs=${rotateSeconds}`);
  };

  // Leaving by the button also leaves fullscreen — walking back to a sidebar that is still
  // covering the whole screen is its own small confusion.
  const exitTv = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => undefined);
    }
    navigate(ANALYTICS_DISPATCH_BOARD_ROUTE);
  };

  /**
   * Hold the screen awake while the board is on the wall.
   *
   * ⚠️ Nothing else on this page counts as activity. The browser's idle timer watches for
   * input, and a display nobody touches for eight hours is exactly what a dispatch board is —
   * so without this the screen dims or sleeps on its own schedule and the board is simply
   * gone until somebody wiggles a mouse.
   *
   * ⚠️ THE LOCK IS DROPPED WHENEVER THE TAB IS HIDDEN and does not return by itself. A screen
   * saver, another tab, a monitor cycling input — any of those silently end it, which is why
   * visibilitychange re-requests rather than assuming one call lasts forever.
   *
   * Refusal is normal and harmless: Firefox has no support, and Chrome declines on battery
   * saver. The board still works; the screen just keeps the operating system's own timeout.
   */
  useEffect(() => {
    if (!tv) {
      return undefined;
    }
    let sentinel: any = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        sentinel = await (navigator as any).wakeLock?.request('screen');
      } catch {
        // Declined. Not an error worth surfacing on a wall nobody is reading for errors.
      }
    };

    const onVisible = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        acquire();
      }
    };

    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      sentinel?.release?.().catch(() => undefined);
    };
  }, [tv]);

  /**
   * 🚨 THE DOCUMENT ITSELF MUST NOT SCROLL ON A WALL DISPLAY. The board is sized to the
   * viewport, but anything that briefly exceeds it — the filter row before it hides, a rail
   * still settling on its fonts — hands the browser a scrollbar down the side of a television,
   * and there is nobody standing in front of it to scroll back.
   *
   * Restored on unmount, so leaving the TV route leaves the rest of the admin panel alone.
   */
  useEffect(() => {
    if (!tv) {
      return undefined;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [tv]);

  /**
   * Remove the controls once nobody is there.
   *
   * Fullscreen and Exit earn their place for the ten seconds somebody spends setting the
   * television up, and are clutter on a wall for the rest of the week. Faded rather than
   * removed, so they come back on the first twitch of a mouse instead of needing a reload.
   */
  const [controlsIdle, setControlsIdle] = useState(false);

  useEffect(() => {
    if (!tv) {
      return undefined;
    }
    let idle: ReturnType<typeof setTimeout>;
    const wake = () => {
      setControlsIdle(false);
      clearTimeout(idle);
      idle = setTimeout(() => setControlsIdle(true), CONTROLS_IDLE_MS);
    };

    wake();
    window.addEventListener('mousemove', wake);
    window.addEventListener('keydown', wake);
    window.addEventListener('touchstart', wake);
    return () => {
      clearTimeout(idle);
      window.removeEventListener('mousemove', wake);
      window.removeEventListener('keydown', wake);
      window.removeEventListener('touchstart', wake);
    };
  }, [tv]);

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  const asOfMoment = asOf ? dayjs(asOf).tz(FIJI) : null;
  const ageMinutes = asOfMoment ? dayjs().tz(FIJI).diff(asOfMoment, 'minute') : null;
  // ⚠️ A FIRST LOAD THAT FAILED IS STALE TOO. With no as_of there is no age, so the old rule
  // read "not stale" and left a blank board with nothing on it saying why — on the one surface
  // that has no toast to fall back on.
  const stale = asOfMoment === null || ageMinutes! >= STALE_AFTER_MINUTES;
  const asOfLabel = asOfMoment ? `${asOfMoment.format('HH:mm')} FJT` : 'unknown';
  const asOfFull = asOfMoment ? asOfMoment.format('ddd D MMM, HH:mm [Fiji]') : 'unknown';

  /**
   * 🚨 NULL IS "NOT KNOWN", NEVER ZERO — the mart says so in as many words, and `dropped ?? 0`
   * broke that promise. Both are null until the tour itself has been ingested, and the card
   * then read "0 of 8 dropped": a 0 invented out of nothing, beside an 8 that was only how
   * many of her orders fall in the window. On a wall that reads as a rider who has delivered
   * nothing all afternoon. Measured 2 Oct 2026: 3 of 180 rows carried a real stop count.
   */
  const tourSummary = (orders: AnalyticsRow[]) => {
    const dropped = num(orders[0], 'dropped_count');
    const stops = num(orders[0], 'stop_count');
    const out = num(orders[0], 'tour_age_min');
    const progress =
      stops === null || dropped === null ? (
        <>{orders.length} orders</>
      ) : (
        <>
          <b>{dropped}</b> of {stops} dropped
        </>
      );
    // tour_age_min is null until the tour itself is ingested — the same rule as the stop
    // counts, and the same answer: say nothing rather than invent a zero.
    return out === null ? (
      progress
    ) : (
      <>
        {progress} · out <b>{formatMinutes(out)}</b>
      </>
    );
  };

  /** How long the rider who has waited LONGEST has been waiting, not an average of the card. */
  const longestWait = (orders: AnalyticsRow[]) => {
    const waits = orders
      .map((o) => num(o, 'assigned_age_min'))
      .filter((n): n is number => n !== null);
    return waits.length ? Math.max(...waits) : null;
  };

  /**
   * One rider's card. `rows` is what to draw — the fitted page on the wall display, the whole
   * list on the page — and `listRef` is what the fitter measures, so it is passed only for the
   * card that is actually being paged.
   */
  const riderCard = (
    rider: string,
    orders: AnalyticsRow[],
    rows: AnalyticsRow[],
    roster: string[],
    kind: 'assigned' | 'active',
    listRef?: React.RefObject<HTMLDivElement | null>,
    urgent?: string[],
  ) => (
    <HeroTour
      key={rider}
      rider={rider}
      summary={kind === 'assigned' ? <>{orders.length} to load</> : tourSummary(orders)}
      farNote={
        kind === 'assigned' && longestWait(orders) !== null ? (
          // ⚠️ THE HEADLINE OBEYS THE SAME RULE AS THE ROWS. It never did, so a load that had
          // sat 55 minutes printed in the body colour while the rule said red past 45
          // (Nikhil, 23 Oct).
          <Box component="span" sx={{ color: ageColour(longestWait(orders), false) }}>
            waiting {formatMinutes(longestWait(orders) as number)}
          </Box>
        ) : null
      }
      // §1.4: no bare durations. Every column names its direction, because "1h 26m" counting
      // down to a slot and "1h 26m" counting up from an assignment are opposite facts.
      leftLabel={
        rows.some(isCnc)
          ? 'c&c first · rest by slot'
          : rows.some(isReturn)
            ? 'returns first · rest by slot'
            : 'by slot'
      }
      rightLabel={kind === 'active' ? 'time left in slot' : 'waiting'}
      roster={roster}
      current={rider}
      alerts={alertRiders}
      urgent={cncRiders}
    >
      <Scroller tv={tv} ref={listRef}>
        <StopList>
          {rows.map((row) => (
            <StopRow
              key={str(row, 'order_id')}
              row={row}
              tv={tv}
              cnc={cncStateByOrder.get(str(row, 'order_id'))}
              showState={kind === 'active'}
              age={
                kind === 'assigned'
                  ? // Shown only when this row differs from the card's figure. A rider's batch
                    // is assigned in one go, so printing the same number on every line is noise
                    // — but a row that genuinely waited longer still says so.
                    num(row, 'assigned_age_min') === longestWait(orders)
                    ? undefined
                    : num(row, 'assigned_age_min')
                  : num(row, 'is_overdue') === 1
                    ? null
                    : num(row, 'slot_remaining_min')
              }
              countdown={kind === 'active'}
            />
          ))}
        </StopList>
      </Scroller>
    </HeroTour>
  );

  const controls = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {/* Bounded at both ends so a nonsense range cannot be produced at all: never further
            back than the mart can answer, never past today, never inverted. */}
        <DatePicker
          label="From"
          value={range.from}
          onChange={(v) => v && setRange((r) => ({ ...r, from: v.startOf('day') }))}
          // ⚠️ MAX_LOOKBACK_DAYS − 1, because the mart's bound is exclusive: 14 days means the
          // 19th through the 2nd, not the 18th through the 2nd. Offering the 18th is a date
          // that can only ever come back empty.
          minDate={dayjs()
            .tz(FIJI)
            .startOf('day')
            .subtract(MAX_LOOKBACK_DAYS - 1, 'day')}
          maxDate={range.to}
          slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
        />
        <DatePicker
          label="To"
          value={range.to}
          onChange={(v) => v && setRange((r) => ({ ...r, to: v.startOf('day') }))}
          minDate={range.from}
          maxDate={dayjs().tz(FIJI).startOf('day')}
          slotProps={{ textField: { size: 'small', sx: { width: 165 } } }}
        />
      </LocalizationProvider>
      <FormControlLabel
        sx={{ mr: 0 }}
        control={<Switch size="small" checked={daylight} onChange={toggleTheme} />}
        label="Daylight"
      />
      {!isFullscreen && (
        <Button size="small" variant="outlined" onClick={tv ? goFullscreen : enterTv}>
          Fullscreen
        </Button>
      )}
      {tv && (
        <Button size="small" variant="text" onClick={exitTv}>
          Exit
        </Button>
      )}
    </Stack>
  );

  return (
    <ThemeProvider theme={boardTheme(daylight, tv)}>
      <Box
        sx={{
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
          // Stated for both, so text and background can never come from two palettes.
          bgcolor: 'background.default',
          ...(tv ? { position: 'fixed', inset: 0, overflow: 'hidden' } : { minHeight: '70vh' }),
        }}
      >
        <Rail
          tv={tv}
          // The roll time is a real fact about the operational day and the reason a delivery at
          // 01:00 belongs to yesterday's shift — worth stating on a board read by whoever is
          // standing in front of it.
          dayLabel={`${range.from.format('ddd D MMM')} – ${range.to.format(
            'ddd D MMM',
          )}, Fiji · day rolls 06:00${truncated ? ' · ⚠ SHOWING THE FIRST 2000 ONLY' : ''}`}
          stale={stale}
          asOfLabel={asOfLabel}
          asOfFull={asOfFull}
          pills={
            <>
              {/* Live states. The prototype also shows `received` and `planning`; the board mart
                excludes both statuses outright, so they are left out rather than faked. */}
              {/* First on the strip, because it is the only thing here that means somebody is
                standing in a car park right now. Absent when nobody is. */}
              {cncOnStrip.length > 0 && (
                <Pill
                  n={cncOnStrip.length}
                  label="C&C"
                  solid
                  title="Click & Collect customers at the gate or in a bay"
                />
              )}
              <Pill n={assigned.length} label="assigned" title="Has a rider, not yet out" />
              <Pill n={active.length} label="executing" tone="ok" title="Out on the road now" />
              {/* ⚠️ A DASH, NOT A ZERO, WHILE THE PACKING FEED IS DEAD. Zero is a measurement —
                it says somebody counted and found none — and on a wall that reads as all-clear.
                Derived, not hardcoded: if no row anywhere carries a packing age the feed is
                down, and the day it returns this becomes a real number on its own. */}
              <Pill
                // ⚠️ A DASH MEANS "WE CANNOT KNOW", and that stops being true the moment a row
                // is visible beneath it. C&C orders reach this panel without the packing feed
                // (§38), so the feed being down no longer implies the panel is empty.
                n={packingFeedLive || waiting.length > 0 ? waiting.length : '—'}
                label="waiting"
                title="Packed, no rider yet. A dash means the packing feed is not connected, not that none are waiting."
              />
              <Pill n={held.length} label="held" tone="warn" title="Parked, in range" />
              {/* Drawn at zero like every other pill. Hidden when empty, a reader cannot tell
                "none are late" from "this board does not show late". */}
              <Pill
                n={overdue}
                label="overdue"
                tone="bad"
                title="Past its slot and still undelivered. Parked orders are excluded — they are held for a customer-side reason."
              />
              <Pill
                n={internal}
                label="internal"
                tone="dim"
                title="Internal and staff orders. Counted in assigned, executing and overdue to the left; excluded from held and from the day totals to the right — this pill is the difference."
              />
              <PillSep />
              {/* Day totals, over the same range. Internal orders are excluded from these three
                and included in the live counts above — the dim pill is what reconciles them. */}
              <Pill
                n={ordersInRange}
                label={ordersInRange === 1 ? 'order' : 'orders'}
                title="All orders in range, internal excluded"
              />
              <Pill
                n={deliveredInRange}
                label="delivered"
                tone="ok"
                title="Completed in range, internal excluded"
              />
              <Pill
                n={cancelledInRange}
                label="cancelled"
                tone="bad"
                title="Cancelled in range, internal excluded"
              />
              <Pill
                n={activeRiders.length}
                label={activeRiders.length === 1 ? 'rider' : 'riders'}
                title="Riders with a tour out now"
              />
            </>
          }
        />

        <CncStrip rows={cncOnStrip} max={CNC_STRIP_MAX} />

        {!(tv && controlsIdle) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              flex: '0 0 auto',
              px: 2,
              pb: 1.5,
            }}
          >
            {controls}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gap: 2,
            px: 2,
            pb: 2,
            position: 'relative',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            /**
             * 🚨 THE ARRANGEMENT, STATED ONCE AND IN FULL.
             *
             * Assigned top-left, the tour beside it down both rows, Waiting under Assigned, then
             * Reassignments and Held back across the bottom. One column when there is no room
             * for two.
             *
             * Named cells rather than a span on the card: expressing it as "the tour spans two
             * rows" left the browser free to place everything else around it, and it stopped
             * spanning — Waiting and Reassignments ended up side by side and Held back alone.
             */
            gridTemplateAreas: {
              xs: `"b-idle" "b-hero" "b-wait" "b-moves" "b-hold"`,
              lg: `"b-idle b-hero" "b-wait b-hero" "b-moves b-hold"`,
            },
            // Three rows on the wall, explicit. Left to size themselves the third row was
            // pushed below the fold and its two cards were simply cut off.
            //
            // ⚠️ THE MIDDLE ROW SHRINKS WHEN WAITING IS EMPTY, and what it gives up goes to row
            // 1 rather than nowhere: Active tour spans rows 1 and 2, so taking from row 2 alone
            // would shrink the tour alongside Waiting. 1.5 + 0.5 is still 2.
            // ⚠️ THE MIDDLE ROW TAKES WHAT IT NEEDS, NOT A FIXED THIRD. Collapsing it only when
            // empty was enough until C&C orders started living there (§38) — one row then held
            // a full third and Assigned was squeezed to a single row across nine pages, which
            // is the complaint this rule was written to fix.
            //
            // Rows 1 and 2 always total 2fr, because Active tour spans both: what Waiting gives
            // up goes to Assigned and the tour never changes height.
            ...(tv && {
              gridTemplateRows:
                waitingRows.length === 0
                  ? 'minmax(0, 1.6fr) minmax(0, 0.4fr) minmax(0, 1fr)'
                  : waitingRows.length <= 2
                    ? 'minmax(0, 1.3fr) minmax(0, 0.7fr) minmax(0, 1fr)'
                    : 'repeat(3, minmax(0, 1fr))',
            }),
          }}
        >
          <MosaicPanel
            id="b-idle"
            tv={tv}
            title="Assigned"
            sub="waiting to be loaded and sent out"
            meta={[
              plural(assigned.length, 'order'),
              plural(assignedRiders.length, 'rider'),
              assignedPaged.pages > 1
                ? `page ${assignedPaged.page + 1}/${assignedPaged.pages}`
                : '',
            ]
              .filter(Boolean)
              .join(' · ')}
            beat={pageSeconds}
            cycle={idleBeat.cycle}
          >
            {assignedRiders.length === 0 ? (
              <Empty>Nothing waiting to go out.</Empty>
            ) : tv && assignedRider ? (
              riderCard(
                assignedRider[0],
                assignedRider[1],
                assignedPaged.shown,
                assignedRiders.map(([name]) => name),
                'assigned',
                assignedPaged.ref,
              )
            ) : (
              assignedRiders.map(([rider, orders]) =>
                riderCard(
                  rider,
                  orders,
                  [...orders].sort(byPriority),
                  assignedRiders.map(([name]) => name),
                  'assigned',
                ),
              )
            )}
          </MosaicPanel>

          <MosaicPanel
            id="b-hero"
            tv={tv}
            title="Active tour"
            sub="out on the road now"
            meta={[
              `${plural(active.length, 'order')} out`,
              activePaged.pages > 1 ? `page ${activePaged.page + 1}/${activePaged.pages}` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
            beat={pageSeconds}
            cycle={heroBeat.cycle}
          >
            {activeRiders.length === 0 ? (
              <Empty>No riders out at the moment.</Empty>
            ) : tv && activeRider ? (
              riderCard(
                activeRider[0],
                activeRider[1],
                activePaged.shown,
                activeRiders.map(([name]) => name),
                'active',
                activePaged.ref,
              )
            ) : (
              activeRiders.map(([rider, orders]) =>
                riderCard(
                  rider,
                  orders,
                  [...orders].sort(byPriority),
                  activeRiders.map(([name]) => name),
                  'active',
                ),
              )
            )}
          </MosaicPanel>

          <MosaicPanel
            id="b-wait"
            tv={tv}
            title="Waiting for a rider"
            sub="no rider yet"
            meta={
              waitingPaged.pages > 1
                ? `${waitingRows.length} waiting · page ${waitingPaged.page + 1}/${waitingPaged.pages}`
                : // ⚠️ NOT "packed" ANY MORE. A C&C order reaches this panel unpacked (§38), so
                  // the old caption asserted something false about the one row most likely to
                  // be here while the packing feed is down.
                  `${waitingRows.length} with no rider`
            }
            beat={pageSeconds}
            cycle={waitBeat.cycle}
          >
            {/* No rider to rotate through — these orders are unassigned by definition — so a
              long list is the only shape this panel comes in, and it pages. */}
            <Scroller tv={tv} ref={waitingPaged.ref}>
              {waitingRows.length === 0 ? (
                <Empty>
                  {packingFeedLive
                    ? 'Nothing packed and waiting for a rider.'
                    : 'No data — the packing feed is not connected, so this panel cannot be filled.'}
                </Empty>
              ) : (
                waitingPaged.shown.map((row) => (
                  <PlainRow
                    key={str(row, 'order_id')}
                    id={str(row, 'order_id')}
                    what={str(row, 'address_text')}
                    ago={num(row, 'packed_age_min')}
                    cnc={cncStateByOrder.get(str(row, 'order_id'))}
                  />
                ))
              )}
            </Scroller>
          </MosaicPanel>

          <MosaicPanel
            id="b-moves"
            tv={tv}
            title="Reassignments"
            sub={`changed hands · ${humanHops} by a person`}
            meta={
              hopsFailed
                ? 'no feed'
                : hopsPaged.pages > 1
                  ? `${visibleHops.length} · page ${hopsPaged.page + 1}/${hopsPaged.pages}`
                  : visibleHops.length
            }
            beat={pageSeconds}
            cycle={movesBeat.cycle}
          >
            <Scroller tv={tv} ref={hopsPaged.ref}>
              {hopsFailed ? (
                <Empty>
                  Could not load reassignments — the board above is unaffected. If this keeps
                  happening, the Analytics Dispatch permission may not cover this view yet.
                </Empty>
              ) : visibleHops.length === 0 ? (
                <Empty>Nothing has changed hands.</Empty>
              ) : (
                hopsPaged.shown.map((row) => (
                  <PlainRow
                    key={`${str(row, 'order_id')}-${str(row, 'hop')}`}
                    // Who decided, not just whether a person did. Locus qualifies the login —
                    // `superstore-devo/personnel/John_Jr` — so the last segment is taken; a
                    // value with no prefix passes through whole.
                    tag={
                      str(row, 'actor_kind') === 'HUMAN'
                        ? str(row, 'actor').split('/').pop()?.trim() || 'person'
                        : 'auto'
                    }
                    kind={str(row, 'actor_kind') === 'HUMAN' ? 'person' : 'auto'}
                    id={str(row, 'order_id')}
                    // Names where we have them, ids where we do not. A rider can appear in an
                    // order's history without having carried one inside the ingest window, so
                    // the name lookup misses — and the id is the fact, never a blank.
                    what={`${str(row, 'from_rider_name') || str(row, 'from_rider_id') || '—'} → ${
                      str(row, 'to_rider_name') || str(row, 'to_rider_id') || '—'
                    }`}
                    ago={num(row, 'hop_age_min')}
                  />
                ))
              )}
            </Scroller>
          </MosaicPanel>

          <MosaicPanel
            id="b-hold"
            tv={tv}
            title="Held back"
            sub={
              olderHeld > 0
                ? `parked — longest waiting first · ${olderHeld} older not shown`
                : 'parked — longest waiting first'
            }
            meta={[
              `${held.filter((r) => str(r, 'hold_kind') !== 'RESTOCK').length} parked`,
              restockCount > 0 ? `${restockCount} to restock` : '',
              heldPaged.pages > 1 ? `page ${heldPaged.page + 1}/${heldPaged.pages}` : '',
              heldInternal > 0 ? `${heldInternal} internal, hidden` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
            beat={pageSeconds}
            cycle={holdBeat.cycle}
          >
            <Scroller tv={tv} ref={heldPaged.ref}>
              {heldSorted.length === 0 ? (
                <Empty>Nothing held back.</Empty>
              ) : (
                heldPaged.shown.map((row) => (
                  <PlainRow
                    key={str(row, 'order_id')}
                    // RESTOCK carries the amber of work waiting on us; PARKED keeps the purple of
                    // work waiting on a customer. The colours were settled from the prototype's
                    // own stylesheet and mean different things.
                    tag={str(row, 'hold_kind') === 'RESTOCK' ? 'restock' : 'parked'}
                    kind={str(row, 'hold_kind') === 'RESTOCK' ? 'restock' : 'parked'}
                    id={str(row, 'order_id')}
                    // ⚠️ The mart coalesces: reason where Locus recorded one, address where it
                    // did not. Measured 21 Oct 2026 — 122 of 199 parked orders fall back, so the
                    // fallback is the COMMON case, not the exception. (An older note here put it
                    // at 1 in 21; that was wrong by eight times.) The browser can tell the two
                    // apart exactly, because the mart sends the address as its own column.
                    what={str(row, 'reason_text')}
                    muted={str(row, 'reason_text') === str(row, 'address_text')}
                    ago={num(row, 'hold_age_min')}
                    cnc={cncStateByOrder.get(str(row, 'order_id'))}
                  />
                ))
              )}
            </Scroller>
          </MosaicPanel>

          {/* 🚨 INSIDE THE GRID ON PURPOSE. The veil covers the board and never the rail: a
            takeover that also hid Fullscreen and Exit left nobody standing at the television
            a way out of it. */}
          {tv && stale && <StaleCage asOfLabel={asOfLabel} />}
        </Box>

        {/* An older range is usually empty, and a blank board reads as a fault unless it says
          otherwise. Completed and cancelled orders are never drawn here, so "nothing from
          20 Sep" is the normal answer rather than a missing one. */}
        {visible.length === 0 && !stale && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', px: 2, pb: 2 }}
          >
            No unfinished work on these days. Completed and cancelled orders are never shown here,
            so an older range is often empty — that is the board working, not a fault.
          </Typography>
        )}
      </Box>
    </ThemeProvider>
  );
};
